import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Cookie, X } from "lucide-react";
import {
  CATEGORIES,
  acceptAll,
  getConsent,
  hasDecided,
  hasOptionalCategories,
  onConsentChange,
  rejectAll,
  setConsent,
} from "@/lib/consent";

/**
 * Banner cookie.
 *
 * Due modalità, decise da src/lib/consent.js:
 *  - nessuna categoria non necessaria (situazione attuale): banner puramente
 *    INFORMATIVO, un solo pulsante "Ho capito". Non chiede un consenso che non
 *    serve, perché i cookie tecnici ne sono esenti (art. 122 D.Lgs 196/2003).
 *  - con categorie non necessarie: banner a scelta, con Accetta / Rifiuta /
 *    Personalizza di pari evidenza grafica (Linee guida Garante 2021).
 *
 * In entrambi i casi la X NON vale come consenso: chiude senza autorizzare
 * nulla e il banner ricomparirà alla prossima visita.
 */
export const CookieBanner = () => {
  const { t } = useLang();
  const c = t.cookies;
  const granular = hasOptionalCategories();

  const [visible, setVisible] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [choices, setChoices] = useState(() =>
    Object.fromEntries(CATEGORIES.map((cat) => [cat.id, false]))
  );

  useEffect(() => {
    if (hasDecided()) return undefined;
    const id = setTimeout(() => setVisible(true), 800);
    return () => clearTimeout(id);
  }, []);

  // Se il consenso viene revocato altrove (link "Preferenze cookie" nel footer)
  // il banner deve tornare a mostrarsi.
  useEffect(
    () =>
      onConsentChange((state) => {
        if (state === null) {
          setChoices(Object.fromEntries(CATEGORIES.map((cat) => [cat.id, false])));
          setPanelOpen(false);
          setVisible(true);
        } else {
          setVisible(false);
        }
      }),
    []
  );

  const close = useCallback(() => {
    setPanelOpen(false);
    setVisible(false);
  }, []);

  // "Ho capito" sul banner informativo: registra la presa visione, non un
  // consenso al tracciamento (che non avviene).
  const acknowledge = () => {
    setConsent({});
    close();
  };

  if (!visible) return null;

  return (
    <div
      data-testid="cookie-banner"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      className="fixed bottom-4 left-4 right-4 md:left-6 md:right-auto md:bottom-6 md:max-w-md z-[60]"
    >
      <div className="bg-white border border-black/10 rounded-3xl shadow-[0_30px_60px_-20px_rgba(0,0,0,0.25)] p-6 md:p-7 relative">
        <button
          data-testid="cookie-close"
          onClick={close}
          aria-label={c.close_aria}
          className="absolute top-4 right-4 text-[#737373] hover:text-[#1A1A1A] rounded-full focus:outline-none focus:ring-2 focus:ring-[#A78BFA]"
        >
          <X size={16} />
        </button>

        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-[#F5F0FF] flex items-center justify-center flex-shrink-0">
            <Cookie size={18} className="text-[#A78BFA]" aria-hidden />
          </div>
          <div>
            <h2
              id="cookie-banner-title"
              className="font-serif text-xl text-[#1A1A1A] tracking-tight"
              data-testid="cookie-title"
            >
              {c.title}
            </h2>
            <p className="mt-2 text-sm text-[#4A4A4A] font-light leading-relaxed">
              {granular ? c.body_granular : c.body_technical}
            </p>
          </div>
        </div>

        {panelOpen && (
          <div className="mt-5 space-y-3" data-testid="cookie-panel">
            <Toggle
              id="necessary"
              title={c.cat_necessary_title}
              desc={c.cat_necessary_desc}
              checked
              locked
              lockedLabel={c.always_on}
            />
            {CATEGORIES.map((cat) => (
              <Toggle
                key={cat.id}
                id={cat.id}
                title={cat.title}
                desc={cat.desc}
                checked={choices[cat.id]}
                onChange={(v) => setChoices((s) => ({ ...s, [cat.id]: v }))}
              />
            ))}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {granular ? (
            <>
              {panelOpen ? (
                <button
                  data-testid="cookie-save"
                  onClick={() => {
                    setConsent(choices);
                    close();
                  }}
                  className="inline-flex items-center bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-5 py-2.5 text-xs tracking-wide transition-colors"
                >
                  {c.save}
                </button>
              ) : (
                <button
                  data-testid="cookie-accept"
                  onClick={() => {
                    acceptAll();
                    close();
                  }}
                  className="inline-flex items-center bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-5 py-2.5 text-xs tracking-wide transition-colors"
                >
                  {c.accept}
                </button>
              )}
              {/* Stessa evidenza grafica di "Accetta": rifiutare non può essere
                  più faticoso dell'accettare. */}
              <button
                data-testid="cookie-reject"
                onClick={() => {
                  rejectAll();
                  close();
                }}
                className="inline-flex items-center bg-white border border-black/10 text-[#1A1A1A] hover:bg-[#FAFAFA] rounded-full px-5 py-2.5 text-xs tracking-wide transition-colors"
              >
                {c.reject}
              </button>
              {!panelOpen && (
                <button
                  data-testid="cookie-customize"
                  onClick={() => {
                    const current = getConsent();
                    if (current) {
                      setChoices(
                        Object.fromEntries(
                          CATEGORIES.map((cat) => [cat.id, current[cat.id] === true])
                        )
                      );
                    }
                    setPanelOpen(true);
                  }}
                  className="inline-flex items-center bg-white border border-black/10 text-[#1A1A1A] hover:bg-[#FAFAFA] rounded-full px-5 py-2.5 text-xs tracking-wide transition-colors"
                >
                  {c.customize}
                </button>
              )}
            </>
          ) : (
            <button
              data-testid="cookie-accept"
              onClick={acknowledge}
              className="inline-flex items-center bg-[#1A1A1A] text-white hover:bg-[#333] rounded-full px-5 py-2.5 text-xs tracking-wide transition-colors"
            >
              {c.ack}
            </button>
          )}

          <Link
            to="/cookie-policy"
            data-testid="cookie-more"
            className="ml-auto text-xs text-[#737373] hover:text-[#1A1A1A] underline underline-offset-4"
          >
            {c.more}
          </Link>
        </div>
      </div>
    </div>
  );
};

const Toggle = ({ id, title, desc, checked, onChange, locked, lockedLabel }) => (
  <div className="flex items-start gap-3 rounded-2xl border border-black/5 bg-[#FAFAFA] px-4 py-3">
    <div className="flex-1">
      <div className="text-sm text-[#1A1A1A]">{title}</div>
      <div className="text-xs text-[#737373] font-light mt-0.5 leading-relaxed">{desc}</div>
    </div>
    {locked ? (
      <span className="text-[10px] uppercase tracking-[0.2em] text-[#A78BFA] mt-1">
        {lockedLabel}
      </span>
    ) : (
      <label className="mt-1 inline-flex items-center cursor-pointer">
        <span className="sr-only">{title}</span>
        <input
          type="checkbox"
          data-testid={`cookie-toggle-${id}`}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="h-4 w-4 accent-[#A78BFA] cursor-pointer"
        />
      </label>
    )}
  </div>
);
